import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

interface ClippedCoupon {
  productId: string;
  couponType: 'dollar' | 'percent';
  couponAmount: number;
  clippedAt: Date;
}

@Injectable({
  providedIn: 'root'
})
export class CouponClipService {
  private readonly STORAGE_KEY = 'clippedCoupons';
  private clippedCouponsSubject = new BehaviorSubject<ClippedCoupon[]>([]);
  public clippedCoupons$ = this.clippedCouponsSubject.asObservable();

  constructor() {
    this.loadClippedCoupons();
  }

  private loadClippedCoupons(): void {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    if (stored) {
      try {
        const coupons = JSON.parse(stored);
        // Convert date strings back to Date objects
        coupons.forEach((c: any) => c.clippedAt = new Date(c.clippedAt));
        this.clippedCouponsSubject.next(coupons);
      } catch (error) {
        console.error('Error loading clipped coupons:', error);
        this.clippedCouponsSubject.next([]);
      }
    }
  }

  private saveClippedCoupons(): void {
    const coupons = this.clippedCouponsSubject.value;
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(coupons));
  }

  clipCoupon(productId: string, couponType: 'dollar' | 'percent', couponAmount: number): void {
    // Validate input parameters
    if (!productId) {
      console.error('clipCoupon: productId is required');
      return;
    }
    
    if (!couponType || (couponType !== 'dollar' && couponType !== 'percent')) {
      console.error('clipCoupon: invalid couponType:', couponType);
      return;
    }
    
    if (couponAmount == null || couponAmount <= 0) {
      console.error('clipCoupon: invalid couponAmount:', couponAmount);
      return;
    }
    
    try {
      const currentCoupons = this.clippedCouponsSubject.value;
      
      // Check if already clipped
      if (!this.isCouponClipped(productId)) {
        const newCoupon: ClippedCoupon = {
          productId,
          couponType,
          couponAmount,
          clippedAt: new Date()
        };
        
        const updatedCoupons = [...currentCoupons, newCoupon];
        this.clippedCouponsSubject.next(updatedCoupons);
        this.saveClippedCoupons();
      }
    } catch (error) {
      console.error('Error clipping coupon:', error);
    }
  }

  unclipCoupon(productId: string): void {
    if (!productId) {
      console.error('unclipCoupon: productId is required');
      return;
    }
    
    try {
      const currentCoupons = this.clippedCouponsSubject.value;
      const updatedCoupons = currentCoupons.filter(c => c.productId !== productId);
      this.clippedCouponsSubject.next(updatedCoupons);
      this.saveClippedCoupons();
    } catch (error) {
      console.error('Error unclipping coupon:', error);
    }
  }

  isCouponClipped(productId: string): boolean {
    const currentCoupons = this.clippedCouponsSubject.value;
    return currentCoupons.some(c => c.productId === productId);
  }

  getClippedCoupon(productId: string): ClippedCoupon | undefined {
    const currentCoupons = this.clippedCouponsSubject.value;
    return currentCoupons.find(c => c.productId === productId);
  }

  getAllClippedCoupons(): ClippedCoupon[] {
    return this.clippedCouponsSubject.value;
  }

  clearAllClippedCoupons(): void {
    this.clippedCouponsSubject.next([]);
    localStorage.removeItem(this.STORAGE_KEY);
  }

  // Calculate savings for a product if its coupon is clipped
  calculateSavings(price: number, productId: string): number {
    try {
      if (price == null || price <= 0 || !productId) {
        return 0;
      }
      
      const clippedCoupon = this.getClippedCoupon(productId);
      if (!clippedCoupon) return 0;

      if (clippedCoupon.couponType === 'dollar') {
        return clippedCoupon.couponAmount; // Already in cents
      } else if (clippedCoupon.couponType === 'percent') {
        // Percentage discount
        return Math.round(price * clippedCoupon.couponAmount / 100);
      }
      
      return 0;
    } catch (error) {
      console.error('Error calculating coupon savings:', error);
      return 0;
    }
  }

  // Get the final price after applying clipped coupon
  getFinalPrice(price: number, productId: string): number {
    try {
      if (price == null || price <= 0) {
        return 0;
      }
      
      const savings = this.calculateSavings(price, productId);
      return Math.max(0, price - savings);
    } catch (error) {
      console.error('Error calculating final price:', error);
      return price || 0;
    }
  }
}